import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable({
  providedIn: 'root',
})
export class MweOAuthService extends OAuthService {
  async createRegistrationUrl({
    registrationUrl,
    state,
    customRedirectUri,
    loginHint,
    noPrompt,
    params = {},
  }: {
    registrationUrl: string;
    state?: string;
    customRedirectUri?: string;
    loginHint?: string;
    noPrompt?: boolean;
    params?: { [key: string]: string };
  }): Promise<string> {
    if (!this.requestAccessToken && !this.oidc) {
      throw new Error('Either requestAccessToken or oidc or both must be true');
    }

    const [redirectUri, nonce] = await Promise.all([Promise.resolve(customRedirectUri || this.redirectUri), this.createAndSaveNonce()]);
    const mutatedState = state ? `${nonce}${this.config.nonceStateSeparator}${encodeURIComponent(state)}` : nonce;

    const responseType = this.config.responseType || this.determineResponseType();

    const scope = this.buildScope();

    const baseParams = this.buildBaseParameters({
      responseType,
      redirectUri,
      mutatedState,
      scope,
      nonce,
      loginHint,
      noPrompt,
    });

    const allParams = {
      ...baseParams,
      ...params,
      ...(this.customQueryParams || {}),
    };

    let url = this.buildUrlWithParams(registrationUrl, allParams);

    if (this.shouldUsePKCE(responseType)) {
      url = await this.addPKCEToUrl(url);
    }

    return url;
  }

  private determineResponseType(): string {
    if (this.oidc && this.requestAccessToken) {
      return 'id_token token';
    } else if (this.oidc && !this.requestAccessToken) {
      return 'id_token';
    } else {
      return 'token';
    }
  }

  private buildScope(): string {
    let scope = this.scope;
    if (this.oidc && !scope.match(/(^|\s)openid($|\s)/)) {
      scope = 'openid ' + scope;
    }
    return scope;
  }

  private buildBaseParameters({
    responseType,
    redirectUri,
    mutatedState,
    scope,
    nonce,
    loginHint,
    noPrompt,
  }: {
    responseType: string;
    redirectUri: string;
    mutatedState: string;
    scope: string;
    nonce: string;
    loginHint?: string;
    noPrompt?: boolean;
  }): Record<string, string | undefined> {
    const baseParams: Record<string, string | undefined> = {
      response_type: responseType,
      client_id: this.clientId,
      state: mutatedState,
      redirect_uri: redirectUri,
      scope,
    };

    if (loginHint) baseParams['login_hint'] = loginHint;
    if (this.resource) baseParams['resource'] = this.resource;
    if (this.oidc) baseParams['nonce'] = nonce;
    if (noPrompt) baseParams['prompt'] = 'none';

    return baseParams;
  }

  private shouldUsePKCE(responseType: string): boolean {
    return responseType.includes('code') && !this.disablePKCE;
  }

  private async addPKCEToUrl(url: string): Promise<string> {
    const [challenge, verifier] = await this.createChallangeVerifierPairForPKCE();

    const storage = this.saveNoncesInLocalStorage && typeof window['localStorage'] !== 'undefined' ? localStorage : this._storage;
    storage.setItem('PKCE_verifier', verifier);

    const urlObj = new URL(url);
    urlObj.searchParams.set('code_challenge', challenge);
    urlObj.searchParams.set('code_challenge_method', 'S256');

    return urlObj.toString();
  }

  private buildUrlWithParams(baseUrl: string, params: Record<string, string | undefined>): string {
    const url = new URL(baseUrl);

    Object.entries(params).forEach(([key, value]) => {
      if (value != null) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  }
}
